/**
 * Meldet, wann nach dem ersten Laden auch der SEITENINHALT hydriert ist.
 *
 * Warum es das braucht: TanStack legt um den Seiteninhalt eine eigene
 * Suspense-Grenze (das `Outlet` der Root-Route, `Match.tsx` im Router-Paket).
 * React hydriert eine solche Grenze in einem späteren Durchgang als die
 * Root-Route. Die Effekte der Root-Route laufen also, BEVOR die Seite selbst
 * hydriert ist. Wer dort Zustand ändert, den die Seite beim Rendern liest,
 * erzeugt React-Fehler #418: So hat die Sprachumschaltung auf `/auth` den Satz
 * „Awaken your Xenomon" gegen das „Erwecke dein Xenomon" des Servers gestellt —
 * live auf jeder öffentlichen Seite, sobald Englisch gewählt war (15.09.2026,
 * `docs/VERLAUF.md`).
 *
 * Das Signal ist das Router-Ereignis `onRendered`. TanStack sendet es aus einem
 * Layout-Effekt, der INNERHALB dieser Grenze sitzt — also erst, wenn sie fertig ist.
 */

/** Nur das, was hier gebraucht wird; der echte Router erfüllt es, und ein Test braucht keinen. */
export type FirstRenderRouter = {
  subscribe: (eventType: "onRendered", fn: () => void) => () => void;
  state: { matches: ReadonlyArray<{ globalNotFound?: boolean }> };
};

let gerendert = false;
const wartend = new Set<() => void>();

/**
 * Beim Anlegen des Routers aufrufen, also VOR dem Hydrieren. Wer sich erst in
 * einem Effekt anmeldet, kann das Ereignis schon verpasst haben.
 */
export function watchFirstPageRender(router: Pick<FirstRenderRouter, "subscribe">): void {
  if (typeof window === "undefined" || gerendert) return;
  const abmelden = router.subscribe("onRendered", () => {
    abmelden();
    gerendert = true;
    const faellig = [...wartend];
    wartend.clear();
    for (const fn of faellig) fn();
  });
}

/**
 * Führt `fn` aus, sobald der Seiteninhalt hydriert ist. Sofort, wenn das schon
 * geschehen ist — oder wenn es keinen eigenen Seiteninhalt gibt: Bei einem 404
 * auf Root-Ebene rendert das `Outlet` die Fehlerseite direkt, ohne Grenze und
 * ohne `onRendered`, und ohne diesen Zweig würde ewig gewartet.
 * Gibt die Abmeldung für den Aufräumer eines Effekts zurück.
 */
export function afterFirstPageRender(
  router: Pick<FirstRenderRouter, "state">,
  fn: () => void,
): () => void {
  const matches = router.state.matches;
  const ohneSeiteninhalt = matches.length < 2 || matches[0]?.globalNotFound === true;
  if (gerendert || ohneSeiteninhalt) {
    fn();
    return () => {};
  }
  wartend.add(fn);
  return () => {
    wartend.delete(fn);
  };
}
